import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import api, { API_URL, getErrorMessage } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ToastProvider';
import { useConfirm } from '../components/ConfirmProvider';
import StateBlock from '../components/StateBlock';
import ItemRow from '../components/lista/ItemRow';
import useKeyedDebounce from '../hooks/useKeyedDebounce';
import { formatCurrency } from '../utils/format';
import { buildItem, filterItems, getListaStats, sumTotal } from '../utils/lista';

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'unchecked', label: 'Pendentes' },
  { value: 'checked', label: 'Comprados' },
];

/** Janela em que uma edição local tem prioridade sobre o eco do socket. */
const LOCAL_EDIT_GRACE = 2500;

const NEW_ITEM = { quantidade: 1, preco: 0, checked: false };

const upsertById = (items = [], item) =>
  items.some((current) => current._id === item._id)
    ? items.map((current) => (current._id === item._id ? item : current))
    : [...items, item];

const mapCategoria = (lista, catId, updater) => ({
  ...lista,
  categorias: (lista.categorias || []).map((categoria) =>
    categoria._id === catId ? updater(categoria) : categoria
  ),
});

const ListaDeCompras = () => {
  const { id: listaId } = useParams();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [lista, setLista] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [novoItem, setNovoItem] = useState('');
  const [novaCategoria, setNovaCategoria] = useState('');
  const [novoItemPorCategoria, setNovoItemPorCategoria] = useState({});
  const [renaming, setRenaming] = useState({ id: null, nome: '' });
  const [pendingAction, setPendingAction] = useState('');

  // Espelho síncrono do estado: permite calcular o próximo item sem depender
  // de efeitos colaterais dentro do updater do setState.
  const listaRef = useRef(null);
  const dirtyRef = useRef(new Map());

  const updateLista = useCallback((updater) => {
    const current = listaRef.current;
    const next = typeof updater === 'function' ? updater(current) : updater;

    if (next === current) {
      return;
    }

    listaRef.current = next;
    setLista(next);
  }, []);

  const markDirty = useCallback((key) => {
    dirtyRef.current.set(key, Date.now());
  }, []);

  /** Ignora atualizações vindas do socket para itens em edição local. */
  const isDirty = useCallback((key) => {
    const editedAt = dirtyRef.current.get(key);

    if (!editedAt) {
      return false;
    }

    if (Date.now() - editedAt > LOCAL_EDIT_GRACE) {
      dirtyRef.current.delete(key);
      return false;
    }

    return true;
  }, []);

  const fetchLista = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }

      try {
        const { data } = await api.get(`/listas/${listaId}`);
        updateLista(data);
        setLoadError('');
      } catch (error) {
        const message = getErrorMessage(error, 'Não foi possível carregar a lista.');
        setLoadError(message);

        if (silent) {
          toast.error(message);
        }
      } finally {
        setLoading(false);
      }
    },
    [listaId, toast, updateLista]
  );

  useEffect(() => {
    if (isAuthenticated) {
      fetchLista();
    }
  }, [fetchLista, isAuthenticated]);

  // Persistência ---------------------------------------------------------

  const saveItem = useCallback(
    async (catId, item) => {
      const path = catId
        ? `/listas/${listaId}/categorias/${catId}/itens/${item._id}`
        : `/listas/${listaId}/itens/${item._id}`;

      try {
        await api.put(path, {
          nome: item.nome,
          quantidade: item.quantidade,
          preco: item.preco,
          checked: item.checked,
        });
      } catch (error) {
        toast.error(getErrorMessage(error, 'Não foi possível salvar a alteração.'));
        fetchLista({ silent: true });
      }
    },
    [fetchLista, listaId, toast]
  );

  const { schedule, run } = useKeyedDebounce(saveItem, 600);

  const patchItem = useCallback(
    (catId, itemId, changes, { immediate = false } = {}) => {
      const current = listaRef.current;

      if (!current) {
        return;
      }

      const categoria = catId
        ? (current.categorias || []).find((item) => item._id === catId)
        : null;

      if (catId && !categoria) {
        return;
      }

      const source = catId ? categoria.itens : current.itens;
      const target = (source || []).find((item) => item._id === itemId);

      if (!target) {
        return;
      }

      const nextItem = buildItem(target, changes, categoria?.tipoDeMedida);
      const key = `${catId || 'root'}:${itemId}`;
      markDirty(key);

      updateLista(
        catId
          ? mapCategoria(current, catId, (cat) => ({
              ...cat,
              itens: cat.itens.map((item) => (item._id === itemId ? nextItem : item)),
            }))
          : {
              ...current,
              itens: current.itens.map((item) => (item._id === itemId ? nextItem : item)),
            }
      );

      // Marcar/desmarcar salva na hora; digitação espera a pausa.
      if (immediate) {
        run(key, catId, nextItem);
      } else {
        schedule(key, catId, nextItem);
      }
    },
    [markDirty, run, schedule, updateLista]
  );

  const removeItem = useCallback(
    async (catId, itemId) => {
      const path = catId
        ? `/listas/${listaId}/categorias/${catId}/itens/${itemId}`
        : `/listas/${listaId}/itens/${itemId}`;

      try {
        await api.delete(path);
        updateLista((current) => {
          if (!current) {
            return current;
          }

          return catId
            ? mapCategoria(current, catId, (cat) => ({
                ...cat,
                itens: cat.itens.filter((item) => item._id !== itemId),
              }))
            : { ...current, itens: current.itens.filter((item) => item._id !== itemId) };
        });
      } catch (error) {
        toast.error(getErrorMessage(error, 'Não foi possível remover o item.'));
      }
    },
    [listaId, toast, updateLista]
  );

  const addItem = useCallback(
    async (event, catId) => {
      event.preventDefault();
      const nome = (catId ? novoItemPorCategoria[catId] : novoItem)?.trim();

      if (!nome) {
        toast.error('Digite o nome do item antes de adicionar.');
        return;
      }

      const actionKey = catId ? `add-item-${catId}` : 'add-item';
      setPendingAction(actionKey);

      try {
        const path = catId
          ? `/listas/${listaId}/categorias/${catId}/itens`
          : `/listas/${listaId}/itens`;
        const { data } = await api.post(path, { nome, ...NEW_ITEM });

        // Aplica a resposta na hora: a lista continua funcionando mesmo se o
        // socket estiver indisponível (rede corporativa, servidor dormindo).
        updateLista((current) => {
          if (!current) {
            return current;
          }

          return catId
            ? mapCategoria(current, catId, (cat) => ({
                ...cat,
                itens: upsertById(cat.itens, data),
              }))
            : { ...current, itens: upsertById(current.itens, data) };
        });

        if (catId) {
          setNovoItemPorCategoria((prev) => ({ ...prev, [catId]: '' }));
        } else {
          setNovoItem('');
        }
      } catch (error) {
        toast.error(getErrorMessage(error, 'Não foi possível adicionar o item.'));
      } finally {
        setPendingAction('');
      }
    },
    [listaId, novoItem, novoItemPorCategoria, toast, updateLista]
  );

  const addCategoria = useCallback(
    async (event) => {
      event.preventDefault();
      const nome = novaCategoria.trim();

      if (!nome) {
        toast.error('Digite o nome da categoria.');
        return;
      }

      setPendingAction('add-categoria');

      try {
        const { data } = await api.post(`/listas/${listaId}/categorias`, { nome });
        updateLista((current) =>
          current
            ? { ...current, categorias: upsertById(current.categorias, { itens: [], ...data }) }
            : current
        );
        setNovaCategoria('');
        toast.success(`Categoria "${data.nome}" criada.`);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Não foi possível criar a categoria.'));
      } finally {
        setPendingAction('');
      }
    },
    [listaId, novaCategoria, toast, updateLista]
  );

  const renameCategoria = useCallback(
    async (event, catId) => {
      event.preventDefault();
      const nome = renaming.nome.trim();

      if (!nome) {
        toast.error('O nome da categoria não pode ficar vazio.');
        return;
      }

      try {
        const { data } = await api.put(`/listas/${listaId}/categorias/${catId}`, { nome });
        updateLista((current) =>
          current
            ? mapCategoria(current, catId, (cat) => ({ ...cat, nome: data.nome }))
            : current
        );
        setRenaming({ id: null, nome: '' });
      } catch (error) {
        toast.error(getErrorMessage(error, 'Não foi possível renomear a categoria.'));
      }
    },
    [listaId, renaming.nome, toast, updateLista]
  );

  const removeCategoria = useCallback(
    async (categoria) => {
      const confirmed = await confirm({
        title: `Excluir "${categoria.nome}"?`,
        message: `Os ${categoria.itens?.length || 0} itens dessa categoria também serão removidos.`,
        confirmLabel: 'Excluir categoria',
        tone: 'danger',
      });

      if (!confirmed) {
        return;
      }

      try {
        await api.delete(`/listas/${listaId}/categorias/${categoria._id}`);
        updateLista((current) =>
          current
            ? {
                ...current,
                categorias: current.categorias.filter((cat) => cat._id !== categoria._id),
              }
            : current
        );
        toast.success('Categoria removida.');
      } catch (error) {
        toast.error(getErrorMessage(error, 'Não foi possível excluir a categoria.'));
      }
    },
    [confirm, listaId, toast, updateLista]
  );

  const toggleMeasure = useCallback(
    async (categoria) => {
      const tipoDeMedida = categoria.tipoDeMedida === 'peso' ? 'unidade' : 'peso';

      try {
        const { data } = await api.put(
          `/listas/${listaId}/categorias/${categoria._id}/tipo-medida`,
          { tipoDeMedida }
        );
        updateLista((current) =>
          current
            ? mapCategoria(current, categoria._id, (cat) => ({
                ...cat,
                tipoDeMedida: data.tipoDeMedida,
              }))
            : current
        );
      } catch (error) {
        toast.error(getErrorMessage(error, 'Não foi possível alterar o tipo de medida.'));
      }
    },
    [listaId, toast, updateLista]
  );

  // Tempo real -----------------------------------------------------------

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const socket = io(API_URL || undefined, { transports: ['websocket', 'polling'] });
    const room = `lista_${listaId}`;
    let hasJoined = false;

    // Entrar na sala a cada conexão cobre também as reconexões (rede móvel
    // oscilando); ao reconectar recarregamos para recuperar o que se perdeu.
    socket.on('connect', () => {
      socket.emit('join', room);

      if (hasJoined) {
        fetchLista({ silent: true });
      }

      hasJoined = true;
    });

    socket.on('item_adicionado', (item) => {
      updateLista((current) =>
        current ? { ...current, itens: upsertById(current.itens, item) } : current
      );
    });

    socket.on('item_atualizado', (item) => {
      if (isDirty(`root:${item._id}`)) {
        return;
      }

      updateLista((current) =>
        current
          ? {
              ...current,
              itens: current.itens.map((entry) => (entry._id === item._id ? item : entry)),
            }
          : current
      );
    });

    socket.on('item_removido', ({ itemId }) => {
      updateLista((current) =>
        current
          ? { ...current, itens: current.itens.filter((item) => item._id !== itemId) }
          : current
      );
    });

    socket.on('categoria_adicionada', (categoria) => {
      updateLista((current) =>
        current
          ? { ...current, categorias: upsertById(current.categorias, { itens: [], ...categoria }) }
          : current
      );
    });

    socket.on('categoria_atualizada', (categoria) => {
      // Só nome e tipo de medida: o corpo completo sobrescreveria itens que
      // podem estar sendo editados agora.
      updateLista((current) =>
        current
          ? mapCategoria(current, categoria._id, (cat) => ({
              ...cat,
              nome: categoria.nome,
              tipoDeMedida: categoria.tipoDeMedida,
            }))
          : current
      );
    });

    socket.on('categoria_removida', ({ catId }) => {
      updateLista((current) =>
        current
          ? { ...current, categorias: current.categorias.filter((cat) => cat._id !== catId) }
          : current
      );
    });

    socket.on('item_categoria_adicionado', ({ catId, item }) => {
      updateLista((current) =>
        current
          ? mapCategoria(current, catId, (cat) => ({
              ...cat,
              itens: upsertById(cat.itens, item),
            }))
          : current
      );
    });

    socket.on('item_categoria_atualizado', ({ catId, item }) => {
      if (isDirty(`${catId}:${item._id}`)) {
        return;
      }

      updateLista((current) =>
        current
          ? mapCategoria(current, catId, (cat) => ({
              ...cat,
              itens: cat.itens.map((entry) => (entry._id === item._id ? item : entry)),
            }))
          : current
      );
    });

    socket.on('item_categoria_removido', ({ catId, itemId }) => {
      updateLista((current) =>
        current
          ? mapCategoria(current, catId, (cat) => ({
              ...cat,
              itens: cat.itens.filter((item) => item._id !== itemId),
            }))
          : current
      );
    });

    socket.on('lista_atualizada', (updated) => {
      updateLista((current) =>
        current && current._id === updated._id ? { ...current, nome: updated.nome } : current
      );
    });

    socket.on('lista_removida', ({ id }) => {
      if (id === listaId) {
        navigate('/listas', {
          replace: true,
          state: { feedback: { type: 'info', text: 'Esta lista foi removida.' } },
        });
      }
    });

    return () => {
      socket.emit('leave', room);
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [fetchLista, isAuthenticated, isDirty, listaId, navigate, updateLista]);

  // Derivados ------------------------------------------------------------

  const stats = useMemo(() => getListaStats(lista), [lista]);
  const filters = useMemo(() => ({ status, search }), [status, search]);
  const itensSemCategoria = useMemo(
    () => filterItems(lista?.itens, filters),
    [lista, filters]
  );
  const isFiltering = status !== 'all' || search.trim() !== '';

  if (loading) {
    return (
      <div className="page">
        <StateBlock busy title="Carregando lista..." />
      </div>
    );
  }

  if (!lista) {
    return (
      <div className="page">
        <StateBlock
          icon="bi-exclamation-triangle"
          title="Não foi possível abrir esta lista"
          description={loadError || 'Tente novamente em instantes.'}
        >
          <Link to="/listas" className="btn btn--primary">
            Voltar para minhas listas
          </Link>
        </StateBlock>
      </div>
    );
  }

  const categorias = lista.categorias || [];

  return (
    <div className="page page--wide page--flow">
      <Link to="/listas" className="back-link">
        <i className="bi bi-arrow-left" aria-hidden="true"></i>
        Minhas listas
      </Link>

      <section className="surface-card card-pad list-hero" aria-label="Resumo da lista">
        <div>
          <span className="eyebrow">Lista de compras</span>
          <h1 className="list-hero__title">{lista.nome}</h1>
          <p className="section-copy">
            {stats.totalItems === 0
              ? 'Adicione o primeiro item para começar.'
              : `${stats.completedItems} de ${stats.totalItems} itens comprados em ${
                  stats.categoryCount
                } ${stats.categoryCount === 1 ? 'categoria' : 'categorias'}.`}
          </p>
        </div>

        <div className="metric-grid">
          <div className="metric">
            <span className="metric__label">Itens</span>
            <strong className="metric__value">{stats.totalItems}</strong>
          </div>
          <div className="metric">
            <span className="metric__label">Comprados</span>
            <strong className="metric__value">{stats.completedItems}</strong>
          </div>
          <div className="metric">
            <span className="metric__label">Falta comprar</span>
            <strong className="metric__value">{formatCurrency(stats.remainingValue)}</strong>
          </div>
          <div className="metric">
            <span className="metric__label">Total</span>
            <strong className="metric__value">{formatCurrency(stats.totalValue)}</strong>
          </div>
        </div>
      </section>

      <div className="list-summary">
        <div
          className="progress list-summary__progress"
          role="progressbar"
          aria-valuenow={stats.progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progresso da lista"
        >
          <div className="progress__bar" style={{ width: `${stats.progress}%` }}></div>
        </div>
        <span className="list-summary__stat">
          <strong>
            {stats.completedItems}/{stats.totalItems}
          </strong>{' '}
          itens
        </span>
        <span className="list-summary__stat">
          <strong>{formatCurrency(stats.totalValue)}</strong>
        </span>
      </div>

      <section className="surface-card card-pad toolbar" aria-label="Ferramentas da lista">
        <div className="toolbar__block">
          <form className="inline-form" onSubmit={(event) => addItem(event)}>
            <label className="visually-hidden" htmlFor="novo-item">
              Novo item sem categoria
            </label>
            <input
              id="novo-item"
              type="text"
              className="input"
              placeholder="Adicionar item (ex.: café)"
              value={novoItem}
              enterKeyHint="done"
              onChange={(event) => setNovoItem(event.target.value)}
            />
            <button
              type="submit"
              className="btn btn--primary"
              disabled={pendingAction === 'add-item'}
            >
              <i className="bi bi-plus-lg" aria-hidden="true"></i>
              Adicionar
            </button>
          </form>

          <form className="inline-form" onSubmit={addCategoria}>
            <label className="visually-hidden" htmlFor="nova-categoria">
              Nova categoria
            </label>
            <input
              id="nova-categoria"
              type="text"
              className="input"
              placeholder="Nova categoria (ex.: hortifruti)"
              value={novaCategoria}
              enterKeyHint="done"
              onChange={(event) => setNovaCategoria(event.target.value)}
            />
            <button
              type="submit"
              className="btn btn--ghost"
              disabled={pendingAction === 'add-categoria'}
            >
              Criar categoria
            </button>
          </form>
        </div>

        <div className="toolbar__block">
          <label className="visually-hidden" htmlFor="busca-item">
            Buscar item
          </label>
          <input
            id="busca-item"
            type="search"
            className="input"
            placeholder="Buscar item na lista"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="chip-scroller" role="group" aria-label="Filtrar itens">
            {STATUS_FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`chip${status === option.value ? ' is-active' : ''}`}
                onClick={() => setStatus(option.value)}
                aria-pressed={status === option.value}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {stats.totalItems === 0 && categorias.length === 0 ? (
        <StateBlock
          icon="bi-cart-plus"
          title="Lista vazia"
          description="Adicione itens soltos ou crie categorias para separar por setor do mercado."
        />
      ) : (
        <div className="list-grid">
          {lista.itens.length > 0 && (
            <section className="surface-card card-pad list-block">
              <div className="list-block__head">
                <div>
                  <h2 className="list-block__title">Sem categoria</h2>
                  <p className="list-block__sub">
                    {itensSemCategoria.length} de {lista.itens.length} itens •{' '}
                    {formatCurrency(sumTotal(lista.itens))}
                  </p>
                </div>
              </div>

              {itensSemCategoria.length > 0 ? (
                <ul className="item-stack">
                  {itensSemCategoria.map((item) => (
                    <ItemRow
                      key={item._id}
                      item={item}
                      onPatch={(changes) => patchItem(null, item._id, changes)}
                      onToggle={() =>
                        patchItem(null, item._id, { checked: !item.checked }, { immediate: true })
                      }
                      onRemove={() => removeItem(null, item._id)}
                    />
                  ))}
                </ul>
              ) : (
                <p className="stack-empty">Nenhum item corresponde ao filtro atual.</p>
              )}
            </section>
          )}

          {categorias.map((categoria) => {
            const itens = filterItems(categoria.itens, filters);
            const isWeight = categoria.tipoDeMedida === 'peso';
            const isRenaming = renaming.id === categoria._id;

            return (
              <section className="surface-card card-pad list-block" key={categoria._id}>
                {isRenaming ? (
                  <form
                    className="inline-panel"
                    onSubmit={(event) => renameCategoria(event, categoria._id)}
                  >
                    <label className="field">
                      <span className="field-label">Nome da categoria</span>
                      <input
                        type="text"
                        className="input"
                        value={renaming.nome}
                        autoFocus
                        onChange={(event) =>
                          setRenaming((prev) => ({ ...prev, nome: event.target.value }))
                        }
                      />
                    </label>
                    <div className="btn-row btn-row--end">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setRenaming({ id: null, nome: '' })}
                      >
                        Cancelar
                      </button>
                      <button type="submit" className="btn btn--primary btn--sm">
                        Salvar
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="list-block__head">
                    <div>
                      <h2 className="list-block__title">{categoria.nome}</h2>
                      <p className="list-block__sub">
                        {itens.length} de {(categoria.itens || []).length} itens •{' '}
                        {formatCurrency(sumTotal(categoria.itens || []))}
                      </p>
                    </div>

                    <div className="list-block__actions">
                      <button
                        type="button"
                        className="chip"
                        onClick={() => toggleMeasure(categoria)}
                        title={
                          isWeight
                            ? 'Mudar para contagem por unidade'
                            : 'Mudar para cobrança por peso'
                        }
                      >
                        <i
                          className={`bi ${isWeight ? 'bi-speedometer2' : 'bi-123'}`}
                          aria-hidden="true"
                        ></i>
                        {isWeight ? 'Peso (kg)' : 'Unidade'}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--icon-sm"
                        onClick={() => setRenaming({ id: categoria._id, nome: categoria.nome })}
                        aria-label={`Renomear categoria ${categoria.nome}`}
                        title="Renomear"
                      >
                        <i className="bi bi-pencil" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--icon-sm"
                        onClick={() => removeCategoria(categoria)}
                        aria-label={`Excluir categoria ${categoria.nome}`}
                        title="Excluir categoria"
                      >
                        <i className="bi bi-trash3" aria-hidden="true"></i>
                      </button>
                    </div>
                  </div>
                )}

                <form className="inline-form" onSubmit={(event) => addItem(event, categoria._id)}>
                  <label className="visually-hidden" htmlFor={`novo-item-${categoria._id}`}>
                    Novo item em {categoria.nome}
                  </label>
                  <input
                    id={`novo-item-${categoria._id}`}
                    type="text"
                    className="input"
                    placeholder="Adicionar nesta categoria"
                    value={novoItemPorCategoria[categoria._id] || ''}
                    enterKeyHint="done"
                    onChange={(event) =>
                      setNovoItemPorCategoria((prev) => ({
                        ...prev,
                        [categoria._id]: event.target.value,
                      }))
                    }
                  />
                  <button
                    type="submit"
                    className="btn btn--primary btn--icon"
                    disabled={pendingAction === `add-item-${categoria._id}`}
                    aria-label={`Adicionar item em ${categoria.nome}`}
                  >
                    <i className="bi bi-plus-lg" aria-hidden="true"></i>
                  </button>
                </form>

                {itens.length > 0 ? (
                  <ul className="item-stack">
                    {itens.map((item) => (
                      <ItemRow
                        key={item._id}
                        item={item}
                        tipoDeMedida={categoria.tipoDeMedida}
                        onPatch={(changes) => patchItem(categoria._id, item._id, changes)}
                        onToggle={() =>
                          patchItem(
                            categoria._id,
                            item._id,
                            { checked: !item.checked },
                            { immediate: true }
                          )
                        }
                        onRemove={() => removeItem(categoria._id, item._id)}
                      />
                    ))}
                  </ul>
                ) : (
                  <p className="stack-empty">
                    {isFiltering
                      ? 'Nenhum item corresponde ao filtro atual.'
                      : 'Categoria ainda sem itens.'}
                  </p>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ListaDeCompras;
